import { useEffect, useRef, useMemo, useState, useCallback } from 'react'
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/services/apiClient'
import type { GroupSummary } from '@/types'
import { getNotificationsEnabled } from '@/services/notificationService'

/**
 * Invisible component that listens to SignalR events for all of the user's groups
 * and shows system notifications immediately (without waiting for push delivery via FCM).
 * Uses the same notification tag as push, so duplicates are replaced rather than stacked.
 */
export function NotificationManager() {
  const { user } = useAuth()

  if (!user?.isAuthenticated) return null
  return <NotificationManagerInner userId={user.userId} />
}

function NotificationManagerInner({ userId }: { userId: string }) {
  const [groupIds, setGroupIds] = useState<string[]>([])
  const [enabled, setEnabled] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const unmountedRef = useRef(false)

  // Fetch groups and notification preference
  useEffect(() => {
    let cancelled = false

    apiClient.get<GroupSummary[]>('/api/groups')
      .then(groups => { if (!cancelled) setGroupIds(groups.map(g => g.id)) })
      .catch(() => {})

    getNotificationsEnabled()
      .then(val => { if (!cancelled) setEnabled(val) })
      .catch(() => {})

    return () => { cancelled = true }
  }, [userId])

  const showNotification = useCallback(async (title: string, body: string, url?: string) => {
    if (!enabled || Notification.permission !== 'granted') return

    const reg = await navigator.serviceWorker?.ready
    if (!reg) return

    await reg.showNotification(title, {
      body,
      icon: '/pwa-192x192.png',
      badge: '/badge-monochrome.svg',
      data: { url },
      tag: url || `connectloop-${Date.now()}`,
    })

    // Update the app badge count based on active notifications
    const notifications = await reg.getNotifications()
    navigator.setAppBadge?.(notifications.length)
  }, [enabled])

  const groupIdKey = useMemo(() => groupIds.join(','), [groupIds])

  useEffect(() => {
    if (!groupIdKey || !enabled) return
    unmountedRef.current = false

    const ids = groupIdKey.split(',')
    const currentUserId = userId

    const connection = new HubConnectionBuilder()
      .withUrl((__API_BASE_URL__ || '') + '/hubs/prayer-group', { withCredentials: true })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .build()

    connection.on('PrayerRequestCreated', (pr) => {
      if (pr.userId !== currentUserId) {
        showNotification(
          'New Prayer Request',
          `${pr.userDisplayName}: ${pr.title}`,
          `/prayer-requests/${pr.id}`,
        )
      }
    })

    connection.on('CommentAdded', ({ prayerRequestId, comment }) => {
      if (comment.userId !== currentUserId) {
        showNotification(
          'New Comment',
          `${comment.userDisplayName}: ${comment.text?.substring(0, 100)}`,
          `/prayer-requests/${prayerRequestId}`,
        )
      }
    })

    connection.on('DiscussionCreated', (disc) => {
      if (disc.userId !== currentUserId) {
        showNotification(
          'New Discussion',
          `${disc.userDisplayName}: ${disc.title}`,
          `/discussions/${disc.id}`,
        )
      }
    })

    connection.on('DiscussionCommentAdded', ({ discussionId, comment }) => {
      if (comment.userId !== currentUserId) {
        showNotification(
          'New Discussion Comment',
          `${comment.userDisplayName}: ${comment.text?.substring(0, 100)}`,
          `/discussions/${discussionId}`,
        )
      }
    })

    connection.on('MemberJoined', ({ groupId, displayName }) => {
      showNotification(
        'New Member',
        `${displayName} joined the group`,
        `/groups/${groupId}`,
      )
    })

    const joinGroups = async () => {
      for (const id of ids) {
        try {
          await connection.invoke('JoinGroup', id)
        } catch (err) {
          console.error(`NotificationManager: Failed to join group ${id}:`, err)
        }
      }
    }

    connection.onreconnected(joinGroups)

    connection.onclose(() => {
      if (!unmountedRef.current) {
        const delay = Math.min(5000 * 2 ** retryCount, 60000)
        setTimeout(() => {
          if (!unmountedRef.current) {
            setRetryCount(c => c + 1)
          }
        }, delay)
      }
    })

    connection.start().then(joinGroups).catch(() => {})

    return () => {
      unmountedRef.current = true
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop()
      }
    }
  }, [userId, groupIdKey, enabled, retryCount, showNotification])

  return null
}
